import {inject} from '@angular/core';
import {
  Actions,
  createEffect,
  ofType,
} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  tap,
  withLatestFrom,
} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ApiService} from '../../../services/api/api.service';
import * as SharedLinkActions from '../shared-link.actions';
import {
  selectSharedLinkData,
  selectSharedLinkId,
} from '../shared-link.selectors';
import {SharedLinkResponse} from '../../../models/shared-link-response.model';

export const loadSharedLinkData$ = createEffect(
  (
    actions$ = inject(Actions),
    apiService = inject(ApiService),
    store = inject(Store),
  ) => actions$.pipe(
    ofType(SharedLinkActions.loadSharedLinkData),
    withLatestFrom(
      store.select(selectSharedLinkData),
      store.select(selectSharedLinkId),
    ),
    filter(([action, existingData, currentId]) => {
      const hasData = !!existingData;
      const idChanged = currentId !== action.id;
      return !hasData || idChanged;
    }),
    mergeMap(([action]) => apiService.getSharedLinkById(action.id).pipe(
      map((response: SharedLinkResponse) => {
        let parsedData = null;
        if (response?.payload) {
          parsedData = JSON.parse(response.payload);
        }
        return SharedLinkActions.loadSharedLinkDataSuccess({
          data: {
            originalResponse: response,
            parsedPayload: parsedData,
          },
        });
      }),
      catchError(error => of(SharedLinkActions.loadSharedLinkDataFailure({
        error: error.message || 'Error loading shared link data',
      }))),
    )),
  ),
  {functional: true},
);

export const setLanguageFromSharedLink$ = createEffect(
  (
    actions$ = inject(Actions),
    translateService = inject(TranslateService),
  ) => actions$.pipe(
    ofType(SharedLinkActions.loadSharedLinkDataSuccess),
    tap(({data}) => {
      const language = data.parsedPayload?.language;
      if (language) {
        translateService.use(language.toLowerCase());
      }
    }),
  ),
  {functional: true, dispatch: false},
);
